// useIncomingIntegrations.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Utilities
import { toast } from 'react-toastify';
import { cleanString } from '../../apiUtils';
import {
    normalizeIncomingAction,
    resolveIncomingFieldAliases,
} from '../utils/integrationUtils';

// Services
import {
    createBaseIncomingHook,
    deleteBaseIncomingHook,
    listBaseIncomingHooks,
    updateBaseIncomingHook,
} from 'services/api/ApiAccessService';

/**
 * useIncomingIntegrations
 *
 * Manages incoming webhook data, modal state, and API-side actions for integrations.
 *
 * @param {string} baseId - The active base identifier.
 * @param {boolean} canManageCredentials - Whether the current user can manage credentials.
 * @param {array} typeRows - Normalized schema type rows.
 * @param {string} incomingSecretHeaderName - The required incoming webhook header name.
 * @param {Function} setErrorMessage - Setter for page-level error messaging.
 * @param {Function} setStatusMessage - Setter for page-level status messaging.
 * @param {Function} setBusyActionKey - Setter for page-level busy action indicator.
 *
 * @returns {object} Incoming integrations state and handlers.
 */
const useIncomingIntegrations = ({
    baseId,
    canManageCredentials,
    typeRows,
    incomingSecretHeaderName,
    setErrorMessage,
    setStatusMessage,
    setBusyActionKey,
}) => {

    // ----- STATE ----- //

    // Incoming Hooks Database State
    const [incomingHooks, setIncomingHooks] = useState([]);

    // Incoming Loading State
    const [incomingLoading, setIncomingLoading] = useState(false);

    // Incoming Create Modal Open State
    const [incomingCreateModalOpen, setIncomingCreateModalOpen] = useState(false);

    // Incoming Draft Name State
    const [incomingDraftName, setIncomingDraftName] = useState('');

    // Incoming Draft Action State
    const [incomingDraftAction, setIncomingDraftAction] = useState('create');

    // Incoming Draft Type ID State
    const [incomingDraftTypeId, setIncomingDraftTypeId] = useState('');

    // Incoming Reveal Modal Open State
    const [incomingRevealModalOpen, setIncomingRevealModalOpen] = useState(false);

    // Created Incoming Hook URL State
    const [createdIncomingHookUrl, setCreatedIncomingHookUrl] = useState('');

    // Created Incoming Hook Secret State
    const [createdIncomingHookSecret, setCreatedIncomingHookSecret] = useState('');

    // Created Incoming Hook Type ID State
    const [createdIncomingHookTypeId, setCreatedIncomingHookTypeId] = useState('');

    // Created Incoming Hook Action State
    const [createdIncomingHookAction, setCreatedIncomingHookAction] = useState('create');

    // Incoming Setup Modal Open State
    const [incomingSetupModalOpen, setIncomingSetupModalOpen] = useState(false);

    // Selected Incoming Hook State
    const [selectedIncomingHook, setSelectedIncomingHook] = useState(null);

    // Incoming Delete Confirm Open State
    const [incomingDeleteConfirmOpen, setIncomingDeleteConfirmOpen] = useState(false);

    // Pending Delete Incoming Hook State
    const [pendingDeleteIncomingHook, setPendingDeleteIncomingHook] = useState(null);


    // ----- MEMOS ----- //

    // Memoized created hook type row.
    const createdIncomingTypeRow = useMemo(() => {
        const targetTypeId = cleanString(createdIncomingHookTypeId).toLowerCase();
        if (!targetTypeId) return null;

        return typeRows.find((row) => cleanString(row?.typeId).toLowerCase() === targetTypeId) || null;
    }, [createdIncomingHookTypeId, typeRows]);

    // Memoized field alias rows for newly created hook setup.
    const createdIncomingFieldRows = useMemo(() => {
        return resolveIncomingFieldAliases(createdIncomingTypeRow?.fields, {
            includeNodeId: createdIncomingHookAction === 'update',
        });
    }, [createdIncomingHookAction, createdIncomingTypeRow]);

    // Memoized header preview with the generated secret.
    const createdIncomingHeaderPreview = useMemo(() => {
        const secret = cleanString(createdIncomingHookSecret);
        return `${incomingSecretHeaderName}: ${secret || '<your-secret>'}`;
    }, [createdIncomingHookSecret, incomingSecretHeaderName]);

    // Memoized selected hook type row for setup modal.
    const selectedIncomingTypeRow = useMemo(() => {
        const targetTypeId = cleanString(selectedIncomingHook?.typeId || selectedIncomingHook?.typeIdLower).toLowerCase();
        if (!targetTypeId) return null;

        return typeRows.find((row) => cleanString(row?.typeId).toLowerCase() === targetTypeId) || null;
    }, [selectedIncomingHook, typeRows]);

    // Memoized field alias rows for selected hook setup.
    const selectedIncomingFieldRows = useMemo(() => {
        return resolveIncomingFieldAliases(selectedIncomingTypeRow?.fields, {
            includeNodeId: normalizeIncomingAction(selectedIncomingHook?.action) === 'update',
        });
    }, [selectedIncomingHook?.action, selectedIncomingTypeRow]);

    // Memoized selected hook URL string.
    const selectedIncomingHookUrl = useMemo(() => {
        return cleanString(selectedIncomingHook?.url);
    }, [selectedIncomingHook]);

    // Memoized selected hook header template preview.
    const selectedIncomingHeaderPreview = useMemo(() => {
        return `${incomingSecretHeaderName}: <your-secret>`;
    }, [incomingSecretHeaderName]);

    // Memoized descending incoming hook rows by update/create timestamp.
    const sortedIncomingHooks = useMemo(() => {
        return [...incomingHooks].sort((a, b) => {
            const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
            const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
            return bTime - aTime;
        });
    }, [incomingHooks]);

    // Memoized delete confirmation message for incoming hooks.
    const incomingDeleteConfirmMessage = useMemo(() => {
        const label = cleanString(pendingDeleteIncomingHook?.name || 'this webhook');
        return `Delete incoming webhook "${label}"? This cannot be undone.`;
    }, [pendingDeleteIncomingHook]);


    // ----- UTILITIES ----- //

    const loadIncomingHooks = useCallback(async () => {
        if (!baseId) return;

        setIncomingLoading(true);
        setErrorMessage('');

        try {
            if (!canManageCredentials) {
                setIncomingHooks([]);
                return;
            }

            const response = await listBaseIncomingHooks({ baseId });
            setIncomingHooks(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to load incoming webhooks.');
        } finally {
            setIncomingLoading(false);
        }
    }, [canManageCredentials, setErrorMessage, baseId]);


    // ----- EFFECTS ----- //

    // Load incoming hooks when base or permission context changes.
    useEffect(() => {
        loadIncomingHooks();
    }, [loadIncomingHooks]);


    // ----- HANDLERS ----- //

    /**
     * Resets incoming webhook draft values to defaults.
     */
    const resetIncomingDraft = useCallback(() => {
        setIncomingDraftName('');
        setIncomingDraftAction('create');
        setIncomingDraftTypeId(typeRows[0]?.typeId || '');
    }, [typeRows]);

    /**
     * Opens the incoming create modal and resets draft state.
     */
    const handleOpenIncomingCreateModal = useCallback(() => {
        if (!canManageCredentials) return;
        setErrorMessage('');
        setStatusMessage('');
        resetIncomingDraft();
        setIncomingCreateModalOpen(true);
    }, [canManageCredentials, resetIncomingDraft, setErrorMessage, setStatusMessage]);

    /**
     * Creates an incoming webhook and opens one-time credential reveal.
     */
    const handleCreateIncomingHook = useCallback(async () => {
        if (!baseId || !canManageCredentials) return;

        const name = cleanString(incomingDraftName);
        const action = normalizeIncomingAction(incomingDraftAction);
        const typeId = cleanString(incomingDraftTypeId);

        if (!name || !typeId) {
            setErrorMessage('Webhook name and target type are required.');
            return;
        }

        setBusyActionKey('create-incoming-hook');
        setErrorMessage('');
        setStatusMessage('');

        try {
            const response = await createBaseIncomingHook({
                baseId,
                name,
                action,
                typeId,
            });

            const createdHookUrl = cleanString(response?.hook?.url);
            const createdSecret = cleanString(response?.signingSecret);
            const createdTypeId = cleanString(response?.hook?.typeId || typeId);
            const createdAction = normalizeIncomingAction(response?.hook?.action || action);

            setCreatedIncomingHookUrl(createdHookUrl);
            setCreatedIncomingHookSecret(createdSecret);
            setCreatedIncomingHookTypeId(createdTypeId);
            setCreatedIncomingHookAction(createdAction);
            setIncomingRevealModalOpen(true);
            setIncomingCreateModalOpen(false);
            toast.success('Incoming webhook created.');
            setStatusMessage('Incoming webhook created. Copy the URL and secret now.');
            await loadIncomingHooks();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to create incoming webhook.');
        } finally {
            setBusyActionKey('');
        }
    }, [
        canManageCredentials,
        incomingDraftAction,
        incomingDraftName,
        incomingDraftTypeId,
        loadIncomingHooks,
        setBusyActionKey,
        setErrorMessage,
        setStatusMessage,
        baseId,
    ]);

    /**
     * Toggles active/paused status for an incoming webhook.
     *
     * @param {object} hook - Incoming webhook row to update.
     */
    const handleToggleIncomingHookStatus = useCallback(async (hook) => {
        const hookId = cleanString(hook?.id || hook?.key);
        if (!baseId || !hookId || !canManageCredentials) return;

        const currentStatus = cleanString(hook?.status || 'active').toLowerCase();
        const nextStatus = currentStatus === 'active' ? 'paused' : 'active';

        setBusyActionKey(`toggle-incoming:${hookId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await updateBaseIncomingHook({
                baseId,
                hookId,
                patch: {
                    status: nextStatus,
                },
            });

            toast.success(`Incoming webhook ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
            setStatusMessage(`Incoming webhook ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
            await loadIncomingHooks();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update incoming webhook status.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadIncomingHooks, setBusyActionKey, setErrorMessage, setStatusMessage, baseId]);

    /**
     * Opens setup modal for the selected incoming hook.
     *
     * @param {object} hook - Incoming webhook row to inspect.
     */
    const handleOpenIncomingSetupModal = useCallback((hook) => {
        setSelectedIncomingHook(hook || null);
        setIncomingSetupModalOpen(true);
    }, []);

    /**
     * Closes incoming setup modal and clears selected hook.
     */
    const handleIncomingSetupModalClose = useCallback(() => {
        setIncomingSetupModalOpen(false);
        setSelectedIncomingHook(null);
    }, []);

    /**
     * Opens delete confirmation for an incoming webhook.
     *
     * @param {object} hook - Incoming webhook row pending delete.
     */
    const handleIncomingDeletePrompt = useCallback((hook) => {
        if (!canManageCredentials) return;
        setPendingDeleteIncomingHook(hook || null);
        setIncomingDeleteConfirmOpen(true);
    }, [canManageCredentials]);

    /**
     * Updates incoming delete confirm open state.
     *
     * @param {boolean} nextOpen - Next open state.
     */
    const handleIncomingDeleteConfirmOpen = useCallback((nextOpen) => {
        setIncomingDeleteConfirmOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setPendingDeleteIncomingHook(null);
        }
    }, []);

    /**
     * Deletes the selected incoming webhook.
     */
    const handleDeleteIncomingHook = useCallback(async () => {
        const hookId = cleanString(pendingDeleteIncomingHook?.id || pendingDeleteIncomingHook?.key);
        if (!baseId || !hookId || !canManageCredentials) return;

        setIncomingDeleteConfirmOpen(false);
        setBusyActionKey(`delete-incoming:${hookId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await deleteBaseIncomingHook({ baseId, hookId });
            toast.success('Incoming webhook deleted.');
            setStatusMessage('Incoming webhook deleted.');
            await loadIncomingHooks();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to delete incoming webhook.');
        } finally {
            setBusyActionKey('');
            setPendingDeleteIncomingHook(null);
        }
    }, [
        canManageCredentials,
        loadIncomingHooks,
        pendingDeleteIncomingHook,
        setBusyActionKey,
        setErrorMessage,
        setStatusMessage,
        baseId,
    ]);

    /**
     * Closes the incoming create modal.
     */
    const handleIncomingCreateModalClose = useCallback(() => {
        setIncomingCreateModalOpen(false);
    }, []);

    /**
     * Closes the incoming reveal modal.
     */
    const handleIncomingRevealModalClose = useCallback(() => {
        setIncomingRevealModalOpen(false);
    }, []);


    // ----- RETURN ----- //

    return {
        incomingLoading,
        sortedIncomingHooks,
        incomingCreateModalOpen,
        incomingDraftName,
        incomingDraftAction,
        incomingDraftTypeId,
        incomingRevealModalOpen,
        createdIncomingHookUrl,
        createdIncomingHookSecret,
        createdIncomingHeaderPreview,
        createdIncomingFieldRows,
        incomingSetupModalOpen,
        selectedIncomingHookUrl,
        selectedIncomingHeaderPreview,
        selectedIncomingFieldRows,
        incomingDeleteConfirmOpen,
        incomingDeleteConfirmMessage,
        setIncomingDraftName,
        setIncomingDraftAction,
        setIncomingDraftTypeId,
        handleOpenIncomingCreateModal,
        handleCreateIncomingHook,
        handleToggleIncomingHookStatus,
        handleOpenIncomingSetupModal,
        handleIncomingSetupModalClose,
        handleIncomingDeletePrompt,
        handleIncomingDeleteConfirmOpen,
        handleDeleteIncomingHook,
        handleIncomingCreateModalClose,
        handleIncomingRevealModalClose,
    };
};

export default useIncomingIntegrations;
